import type { MembershipCategoryConfiguration, MembershipCategorySectionConfig } from "./types"
import type { MembershipApplicationStepId } from "@/features/membership/application/types"

const baseSections: Record<MembershipApplicationStepId, MembershipCategorySectionConfig> = {
  category: {
    stepId: "category",
    isVisible: true,
    isRequired: true,
    guidance: "Select the membership category you are applying for and confirm you meet the prerequisites.",
  },
  profile: {
    stepId: "profile",
    isVisible: true,
    isRequired: true,
  },
  requirements: {
    stepId: "requirements",
    isVisible: true,
    isRequired: false,
    guidance: "Complete any category-specific requirements that apply to your application.",
  },
  employment: {
    stepId: "employment",
    isVisible: true,
    isRequired: true,
    guidance: "Provide your current role and recent employment history.",
  },
  education: {
    stepId: "education",
    isVisible: true,
    isRequired: true,
    guidance: "List completed qualifications in reverse chronological order.",
  },
  affiliations: {
    stepId: "affiliations",
    isVisible: true,
    isRequired: false,
    guidance: "Professional bodies, memberships, and awards help us understand your network.",
  },
  documents: {
    stepId: "documents",
    isVisible: true,
    isRequired: true,
    guidance: "Upload identification, CV, and any supporting certificates.",
  },
  review: {
    stepId: "review",
    isVisible: true,
    isRequired: true,
    guidance: "Confirm all information before submission.",
  },
}

type SectionOverrides = Partial<
  Record<MembershipApplicationStepId, Partial<MembershipCategorySectionConfig>>
>

function createSections(overrides: SectionOverrides = {}): Record<MembershipApplicationStepId, MembershipCategorySectionConfig> {
  const entries = Object.entries(baseSections) as Array<[
    MembershipApplicationStepId,
    MembershipCategorySectionConfig,
  ]>

  const initialValue: Record<MembershipApplicationStepId, MembershipCategorySectionConfig> = {
    profile: { ...baseSections.profile },
    category: { ...baseSections.profile },
    requirements: { ...baseSections.requirements },
    employment: { ...baseSections.employment },
    education: { ...baseSections.education },
    affiliations: { ...baseSections.affiliations },
    documents: { ...baseSections.documents },
    review: { ...baseSections.review },
  }

  return entries.reduce<Record<MembershipApplicationStepId, MembershipCategorySectionConfig>>(
    (accumulator, [stepId, config]) => {
      const override = overrides[stepId]
      accumulator[stepId] = override
        ? { ...config, ...override, stepId }
        : { ...config }
      return accumulator
    },
    initialValue,
  )
}

export const membershipCategoryConfigurationMap: Record<string, MembershipCategoryConfiguration> = {
  general: {
    key: "general",
    title: "General membership",
    summary: "Complete the standard application steps to provide a well-rounded view of your background.",
    prerequisites: [
      "Valid identification document",
      "Updated curriculum vitae",
    ],
    supportingDocuments: ["Government-issued ID", "Recent CV"],
    sections: createSections(),
  },
  student: {
    key: "student",
    title: "Student membership",
    summary:
      "Designed for actively enrolled students seeking access to learning resources and mentorship programmes.",
    prerequisites: [
      "Proof of active enrolment",
      "Valid student identification number",
      "Expected graduation year",
    ],
    supportingDocuments: ["Student identification card", "Letter of enrolment"],
    sections: createSections({
      profile: {
        requiredFields: ["studentId", "institution", "graduationYear"],
        guidance: "Provide your student credentials so we can verify your enrolment status.",
      },
      employment: {
        isVisible: false,
        isRequired: false,
        guidance: "Employment history is optional for student members.",
      },
      education: {
        guidance: "List your current programme along with any completed qualifications.",
      },
      documents: {
        guidance: "Upload proof of enrolment and your student ID.",
      },
    }),
  },
  professional: {
    key: "professional",
    title: "Professional membership",
    summary:
      "For practising professionals who need to demonstrate experience, qualifications, and ongoing engagement in the field.",
    prerequisites: [
      "Minimum stated years of professional experience",
      "Documented employment history",
      "Professional license where applicable",
    ],
    supportingDocuments: [
      "Professional license (where applicable)",
      "Reference letters or employment confirmation",
    ],
    sections: createSections({
      profile: {
        requiredFields: ["organisation", "placeOfResident", "yearsOfExperience", "professionalLicenseNumber"],
        guidance: "Summarise your professional profile including current employer and licensing details.",
      },
      employment: {
        isVisible: true,
        isRequired: true,
        guidance: "Provide at least three years of progressive professional experience.",
      },
      education: {
        guidance: "List qualifications that support your professional practice.",
      },
      affiliations: {
        isRequired: true,
        guidance: "Include memberships in professional bodies and any industry awards.",
      },
      documents: {
        guidance: "Upload your CV, license copies, and any supporting certificates.",
      },
    }),
  },
  corporate: {
    key: "corporate",
    title: "Corporate membership",
    summary:
      "Tailored for organisations seeking institutional membership and access to industry programmes.",
    prerequisites: [
      "Registered company or organisation",
      "Active trading license",
      "Statement of employee count and business sector",
    ],
    supportingDocuments: [
      "Certificate of incorporation",
      "Trading license",
      "Corporate profile document",
    ],
    sections: createSections({
      profile: {
        requiredFields: ["organisation", "companyRegistrationNumber", "companySize", "businessSector"],
        guidance: "Share organisation details so we can profile your corporate membership.",
      },
      employment: {
        isVisible: false,
        isRequired: false,
        guidance: "Employment history is not required for corporate applications.",
      },
      education: {
        isVisible: false,
        isRequired: false,
        guidance: "Education history is not required for corporate applications.",
      },
      affiliations: {
        guidance: "List any associations or partnerships relevant to your organisation.",
      },
      documents: {
        guidance: "Upload incorporation certificates, licenses, and company profiles.",
      },
    }),
  },
}

export const defaultMembershipCategoryKey = "general"
